<template>
  <master-layout
    :show-footer="false"
    id="courses-list-content"
    v-theme-scope="themeScope"
    background-color="var(--courses-surface)"
  >
    <template #sidebar>
      <CoursesSidebar content-id="courses-list-content" :menu-id="pageMenuId" />
    </template>
    <template #header>
      <CoursesHeader
        :menu-id="pageMenuId"
        content-id="courses-list-content"
        :custom-back="handleBack"
        :is-dark="isDark"
        show-theme-toggle
        @toggle-theme="toggleTheme"
      />
    </template>

    <div class="w-full min-h-full pb-8 bg-[var(--courses-surface)] transition-colors duration-300">
      <!--
        Reading-width container. The tinted background stays full-bleed (outer
        div) while the content is capped and centred, so on an iPad the page
        doesn't stretch every card and heading across the full 820-1032pt.
        `max-w-2xl` (672px) matches CoursesFooter and the story page — one
        reading width across the whole Courses surface.
      -->
      <div class="mx-auto w-full max-w-2xl">
        <!-- Title -->
        <div class="px-6 pt-4 tablet:pt-8 pb-1">
          <!-- Steps on tablet to match the reader's H1, which already scales
               (text-2xl sm:text-3xl md:text-4xl). `tablet:` is 744px so iPad
               mini portrait is included. -->
          <h1
            class="text-[1.75rem] tablet:text-display-xl font-black leading-tight transition-colors duration-300"
            :class="isDark ? 'text-white' : 'text-[#2B2A28]'"
          >
            {{ programName }}
          </h1>
          <p
            v-if="programSubtitle"
            class="text-[0.6875rem] tablet:text-caption-lg font-semibold tracking-[0.2em] uppercase mt-1 transition-colors duration-300 text-[var(--courses-ink-muted)]"
          >
            {{ programSubtitle }}
          </p>
        </div>

        <!-- Student / Leader toggle — leaders only. Drives the store `view`,
           which sets how lessons open (?view=leader) in the reader. -->
        <div v-if="isLeader" class="px-6 pt-4">
          <div
            class="relative inline-flex max-w-full items-center gap-1 rounded-full border-[1.5px] p-1 transition-colors duration-300"
            :class="isDark ? 'bg-[#38332E] border-[#646160]' : 'bg-[#F3EFE8] border-brown-300'"
            role="group"
            aria-label="Content view"
          >
            <!-- Sliding selected-pill background. Absolute, not a flex child:
                 animating left/right (not transform) lets it size itself as
                 "half the padded inner box, minus a small gap" purely in CSS
                 — matching the gap-1 between the two buttons on top — and
                 slide between the two positions without measuring either
                 button's width in JS. Only works because both buttons are
                 flex-1 (exactly half each), so the 50% split always lines
                 up with whichever one is selected. -->
            <span
              class="absolute inset-y-1 rounded-full bg-[#FFE34E] transition-[left,right] duration-300 ease-out"
              :class="view === 'leader' ? 'left-1/2 right-1' : 'left-1 right-1/2'"
              aria-hidden="true"
            />
            <button
              type="button"
              :class="[
                'relative z-10 flex-1 inline-flex items-center justify-center px-5 tablet:px-7 min-h-[40px] tablet:min-h-[48px] rounded-full text-sm tablet:text-base font-semibold transition-colors',
                view === 'student'
                  ? isDark
                    ? 'text-[#1E1B17]'
                    : 'text-[#775A00]'
                  : isDark
                    ? 'text-[#B8B0A9]'
                    : 'text-[#938272]',
              ]"
              :aria-pressed="view === 'student'"
              @click="setView('student')"
            >
              Student
            </button>
            <button
              type="button"
              :class="[
                'relative z-10 flex-1 inline-flex items-center justify-center px-5 tablet:px-7 min-h-[40px] tablet:min-h-[48px] rounded-full text-sm tablet:text-base font-semibold transition-colors',
                view === 'leader'
                  ? isDark
                    ? 'text-[#1E1B17]'
                    : 'text-[#775A00]'
                  : isDark
                    ? 'text-[#B8B0A9]'
                    : 'text-[#938272]',
              ]"
              :aria-pressed="view === 'leader'"
              @click="setView('leader')"
            >
              Leader
            </button>
          </div>
        </div>

        <!-- Progress — one continuous bar across this program's lessons. Right
             below the toggle: it depends only on totalLessons (falsy until
             data loads), not the `loading` flag, so it's safe to sit ahead of
             the loading check the same way Leader's Dashboard used to.
             Each lesson contributes up to a full share: complete = full,
             ≥1 blank filled (in progress) = half. See programProgressPercent. -->
        <div v-if="totalLessons && view !== 'leader'" class="px-6 pt-3 pb-4">
          <p
            class="text-sm tablet:text-base font-normal mb-2 transition-colors duration-300 text-[var(--courses-ink-muted)]"
          >
            {{ progressLabel }}
          </p>
          <div class="h-2 w-full rounded-full overflow-hidden bg-[var(--courses-track)]">
            <div
              class="h-full rounded-full bg-[#FFE34E] transition-all duration-300"
              :style="{ width: programProgressPercent + '%' }"
            />
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="flex justify-center py-16">
          <ion-spinner :color="isDark ? 'light' : 'dark'" />
        </div>

        <template v-else>
          <!-- 3D lesson carousel — shifted up to sit right below the toggle
               (and the progress bar above), ahead of Leader's Dashboard/
               buy-book, rather than after them. Breaks out of the
               reading-width column: the stage spans roughly 2.67x the card
               width (sideOffset is cardW * 0.95, plus the rotated
               neighbour's half-width), so penning it inside 672px chopped
               the side cards mid-word. Text stays capped; the hero gets the
               room. -->
          <div
            v-if="carouselCards.length"
            class="py-6 relative left-1/2 -translate-x-1/2 w-screen max-w-[100vw]"
          >
            <CoursesCarousel
              :cards="carouselCards"
              :current-index="carouselIndex"
              :is-dark="isDark"
              :button-label="view === 'leader' ? 'View Lesson' : 'Begin Lesson'"
              @index-change="carouselIndex = $event"
              @navigate="openLessonPath"
              @locked-tap="handleLockedTap"
            />
          </div>

          <!-- Empty state -->
          <div v-else class="text-center py-12">
            <p class="text-[var(--courses-ink-muted)]">No lessons available yet.</p>
          </div>

          <!-- Leader's Dashboard entry, per the Claude Design prototype: a card
             below the carousel — not a third segment in the toggle, which read
             as a third audience.

             It is NOT gated on the Student/Leader view any more. `view`
             persists and defaults to 'student', so a newly-promoted PCGL
             opened Next Steps and saw no way in at all until they happened to
             flip a toggle that advertises nothing about a dashboard. Nor on
             `isLeader`, which answers leader-GUIDE entitlement — a different
             permission system ANDed onto this one for no reason. canDisciple
             plus the flag is the gate.

             Inside this v-else block (it used to render ahead of the loading
             check, immediately): it sits below the carousel visually, so
             gating it on the same `loading` state avoids the carousel
             popping in above an already-visible tile once data arrives. -->
          <div v-if="leaderDashboardEnabled" class="px-6 pt-3.5">
            <!-- Same chrome as the resource card below (white/bordered, amber
                 icon tile): the two are a leader's entry points to everything
                 that is not a lesson, and reading as siblings is the point. -->
            <button
              type="button"
              class="w-full flex items-center gap-3.5 px-[18px] py-[15px] rounded-[20px] text-left border transition-colors duration-300"
              :class="actionTileClass"
              @click="goToLeaderDashboard"
            >
              <span
                class="w-[42px] h-[42px] shrink-0 rounded-xl flex items-center justify-center"
                :class="actionTileIconChipClass"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M23 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11"
                    stroke="#B4732C"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              <span class="flex-1 min-w-0">
                <b class="block text-[15.5px] font-extrabold">Leader's Dashboard</b>
              </span>
              <!-- A "New" tag, not a count: it says "something inside is waiting
                   on you" — finished lessons nobody has confirmed — and the
                   dashboard says how many. Same yellow as the toggle thumb and
                   progress bar above; absent when the roster is quiet or has not
                   loaded. The count survives in the label for screen readers. -->
              <span
                v-if="readyToConfirmCount"
                class="shrink-0 rounded-full px-2 py-[3px] text-[11px] font-extrabold leading-none uppercase tracking-wide bg-[#FFE34E] text-[#5A4700]"
                :aria-label="`New: ${readyToConfirmCount} to confirm`"
                data-testid="leader-ready-tag"
              >
                New
              </span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                class="shrink-0 opacity-70"
                aria-hidden="true"
              >
                <path
                  d="M9 18l6-6-6-6"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>

          <!-- Standalone leader resources — "Leader's Resource: Stories of HOGC".
               Directly under the Leader's Dashboard, per the design: the two are a
               leader's entry points to everything that is not a lesson.

               Rendered straight off the store list with no leader check of its own.
               The API returns [] to anyone without leader-guide access, so an empty
               list is the gate; a second client-side check would be a second thing
               that can disagree with the server, and the direction it fails in is
               showing leader-only material to a member. This tile is now the
               reference palette for the family: Leader's Dashboard and the buy-book
               tile below both match its bg/border/text and amber icon chip, so all
               three read as one set rather than this one standing apart. Colours
               here are still isDark ternaries, not --courses-* tokens — no existing
               token reproduces this exact white-card-on-sunken-page look, so
               migrating properly (new --courses-surface-raised/-border tokens) is a
               follow-up, not part of this colour-matching pass. -->
          <div v-for="resource in leaderResources" :key="resource.slug" class="px-6 pt-4">
            <button
              type="button"
              class="w-full flex items-center gap-3.5 px-[18px] py-[15px] rounded-[20px] text-left border transition-colors duration-300"
              :class="actionTileClass"
              @click="openResource(resource)"
            >
              <span
                class="w-[42px] h-[42px] shrink-0 rounded-xl flex items-center justify-center"
                :class="actionTileIconChipClass"
              >
                <!-- Open book, per the design — a resource to read, distinct from
                     the dashboard's people mark. -->
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M4 19.5A2.5 2.5 0 0 0 6.5 22H20V2H6.5A2.5 2.5 0 0 0 4 4.5v15z"
                    stroke="#B4732C"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              <span class="flex-1">
                <b class="block text-[15.5px] font-extrabold">{{ resource.title }}</b>
              </span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                class="shrink-0 opacity-70"
              >
                <path
                  d="M9 18l6-6-6-6"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>

          <!-- Buy the book → runs the payment module; on success the course unlocks
             instantly and a book-collection voucher lands in My Vouchers. -->
          <div v-if="canBuyBook" class="mx-6 mt-4">
            <button
              type="button"
              class="w-full flex items-center gap-4 rounded-2xl p-4 text-left border transition-colors duration-300"
              :class="[actionTileClass, isPurchasing ? 'opacity-60' : '']"
              :disabled="isPurchasing"
              :aria-busy="isPurchasing ? 'true' : 'false'"
              @click="handleBuyBook"
            >
              <!-- Same amber badge as Leader's Dashboard / Stories of HOGC above —
                   this tile family shares one icon treatment. -->
              <div
                class="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                :class="actionTileIconChipClass"
              >
                <ion-icon
                  :icon="bookOutline"
                  class="text-[1.375rem] text-[#B4732C]"
                  aria-hidden="true"
                />
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-[1rem] tablet:text-text-body-lg font-semibold leading-tight">
                  Purchase {{ programName }} here
                </p>
                <!-- No price here. The payment sheet states the amount at the
                     moment it is being agreed to, and it takes the figure the
                     server just returned — this card can only show a cached one,
                     so quoting it twice risks showing one number and charging
                     another.

                     text-body-small-md/-lg: one step up the same design-system
                     scale from the previous text-[0.75rem]/-md pair — the old
                     12px/14px read too small next to the 16px/18px title.
                     opacity-70, not -60: -60 measured 4.41:1 in light mode,
                     just under WCAG AA's 4.5:1 for normal text (dark mode was
                     fine at 6.5:1) — -70 clears both themes (6.13:1 / 8.39:1). -->
                <p
                  class="text-[0.875rem] tablet:text-text-body-small-lg font-medium mt-0.5 opacity-70"
                >
                  {{
                    isPurchasing
                      ? 'Opening payment…'
                      : 'Access all lessons on your app! Collect your booklet at the bookstore'
                  }}
                </p>
              </div>
              <!-- Opening a transaction and a hosted checkout is a real round
                   trip, so the arrow becomes a spinner rather than the card only
                   dimming — a dimmed card reads as "disabled", not "working". -->
              <ion-spinner
                v-if="isPurchasing"
                class="shrink-0 w-[18px] h-[18px]"
                :color="isDark ? 'light' : 'dark'"
              />
              <!-- Chevron, matching the Leader's Dashboard tile's icon above
                   exactly: same size, weight, currentColor + opacity-70. -->
              <svg
                v-else
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                class="shrink-0 opacity-70"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>

          <!-- The other way to unlock this program: a printed card from a book
               bought at the IDR counter. Sits under the buy CTA because it
               answers the same question — "how do I get in?" — for someone who
               has already paid, and is hidden once they are FULL, when there is
               nothing left to unlock. -->
          <div v-if="canEnterCode" class="mx-6 mt-3 flex justify-center">
            <button
              type="button"
              class="px-2 py-2 text-[0.8125rem] font-bold underline underline-offset-4 transition-colors duration-300 text-[var(--courses-ink-muted)] active:opacity-70"
              @click="showCodeSheet = true"
            >
              Have a code?
            </button>
          </div>
        </template>
      </div>
    </div>

    <HaveACodeSheet v-model="showCodeSheet" @redeemed="onRedeemed" @needs-cg="onNeedsCgLink" />
    <CgHardBlockModal
      :model-value="needsCgHardBlock || cgRedeemPending"
      @linked="onCgLinked"
      @exit="onCgBlockExit"
    />
    <PurchaseIneligibleModal
      :model-value="showPurchaseIneligibleModal"
      @close="onPurchaseIneligibleClose"
    />
  </master-layout>
</template>

<script>
import { bookOutline } from 'ionicons/icons';
import { toastController } from '@ionic/vue';
import { mapState, mapGetters } from 'vuex';
import CoursesCarousel from './components/CoursesCarousel.vue';
import CoursesHeader from './components/CoursesHeader.vue';
import CoursesSidebar from './components/CoursesSidebar.vue';
import CgHardBlockModal from './components/CgHardBlockModal.vue';
import HaveACodeSheet from './components/HaveACodeSheet.vue';
import PurchaseIneligibleModal from './components/PurchaseIneligibleModal.vue';
import { rebindCoursesMenu } from '@/utils/courses-menu';
import { readyToConfirm } from './leader/lesson-progress';
import { AUTHENTICATED_HOME_PATH } from '@/utils/post-login-navigation';

// The Next Steps part chooser; used only when nothing is stacked behind us.
const NEXT_STEPS_CHOOSER_PATH = '/courses';
import { PaymentStatus, MOBILE_PAYMENTS_FLAG } from '@/constants/Payment/payment-methods';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { resolveProgramName } from '@/utils/program-name';
import coursesTheme from '@/mixins/CoursesTheme';
import cgHardBlock from '@/mixins/CgHardBlock';
import directedNavigation from '@/mixins/DirectedNavigation';
import privateRegion from '@/mixins/PrivateRegion';

export default {
  mixins: [privateRegion, coursesTheme, directedNavigation, cgHardBlock],

  // NOTE on the isDark uses left in this template: the three action tiles
  // (leader dashboard, Stories of HOGC / leader resources, buy-book CTA) share
  // one hand-picked palette — a white-on-sunken-page card with an amber icon
  // chip — that predates the --courses-* token set and has no equivalent
  // there yet, so all three read it off `isDark` rather than a token. See the
  // comment on the leader-resources loop for why that migration is a
  // follow-up rather than part of this pass. The remaining ternaries are the
  // ones a token cannot express regardless:
  //   - <ion-spinner :color>, which takes an Ionic colour NAME, not a CSS value;
  //   - :is-dark passed to children that need the boolean itself.
  name: 'CoursesIndex',
  components: {
    CoursesCarousel,
    CoursesHeader,
    CoursesSidebar,
    CgHardBlockModal,
    HaveACodeSheet,
    PurchaseIneligibleModal,
  },
  data() {
    return {
      loading: false,
      carouselIndex: 0,
      pageMenuId: 'courses-list-menu',
      bookOutline,
      // Guards a double-tap on the buy CTA. Without it, two taps open two
      // transactions and two checkouts — the second sheet resolves the first's
      // promise as cancelled and leaves an orphan row behind.
      isPurchasing: false,
      showCodeSheet: false,
      // The "interested to sign up?" modal, shown when an N/V (who can see the
      // course but not buy it) tries to sign up. See purchaseBlockedByRole.
      showPurchaseIneligibleModal: false,
    };
  },
  computed: {
    // Gates the Admin pill on the same flag as the route it opens. Without this
    // the pill would still render once the flag is off, and tapping it would
    // bounce the leader back to Home — a dead control reads as a bug.
    // Both halves of the same question: does the feature exist for this build,
    // and is this leader eligible to disciple. Gating the card on isLeader alone
    // showed it to anyone with leader-guide access — a looser bar — so leaders
    // below PCGL/POGL tapped through to a dashboard that could only error. These
    // two are also the WHOLE gate: matching the route guard exactly, so the card
    // is visible in precisely the cases where tapping it works.
    leaderDashboardEnabled() {
      // Resolved inside the computed, not at module scope: a module-scope
      // useFeatureFlags() call runs at import time and trips specs that mock the
      // composable with a hoisted jest.mock. Matches canBuyBook below.
      const { isEnabled } = useFeatureFlags();
      return isEnabled('leader-dashboard') && this.canDisciple;
    },
    canDisciple() {
      return !!this.leaderContext?.canDisciple;
    },
    // Shared card palette for the Leader's Dashboard / Stories of HOGC (leader
    // resources) / buy-book tiles — one source instead of the same ternary typed
    // three times, which is how the buy-book tile's copy of it could quietly go
    // stale the next time this palette changes. Still isDark ternaries rather
    // than --courses-* tokens — see the leader-resources loop's own comment on
    // why that migration is a follow-up.
    actionTileClass() {
      return this.isDark
        ? 'bg-[#302D2A] border-[#6B6460] active:bg-[#37332F] text-[#FBF7EE]'
        : 'bg-white border-[#D5CFC8] active:bg-[#F2EFED] text-[#1E1B17]';
    },
    // The amber icon chip these same three tiles share. Alpha drops in dark mode
    // (not just a straight reuse of the light-mode value): at 0.16 against the
    // dark tile fill (#302D2A) the #B4732C icon measures ~2.6:1, under the 3:1
    // WCAG AA minimum for graphical objects; 0.08 clears it at ~3.05:1. Light
    // mode's 0.16 already clears 3:1 (~3.45:1) against the white tile, so it's
    // left as-is — this is a dark-mode-only fix, not a redesign.
    actionTileIconChipClass() {
      return this.isDark ? 'bg-[rgba(224,168,46,0.08)]' : 'bg-[rgba(224,168,46,0.16)]';
    },
    /**
     * This program's standalone leader resources, for the cards under the
     * dashboard. Empty for anyone the API does not serve them to — which is the
     * whole gate; see the template.
     *
     * Not gated on `leaderDashboardEnabled` either: that pairs the
     * `leader-dashboard` flag with canDisciple (PCGL/POGL and above), and reading a
     * resource is not discipling. A leader with leader-guide access can read it
     * whether or not they run a dashboard.
     */
    leaderResources() {
      return this.programResources(this.programSlug);
    },
    ...mapState('courses', ['lessons', 'progress', 'leaderContext', 'students']),
    /**
     * Finished lessons on this leader's roster that nobody has confirmed — the
     * badge on the dashboard card. Summed over members: three members with one
     * lesson each and one member with three both read "3 to confirm", because
     * that is three taps either way. Reads the syllabus off leaderContext, as
     * the dashboard itself does, so an 8-lesson roster is counted on 8.
     */
    readyToConfirmCount() {
      if (!this.leaderDashboardEnabled) return 0;
      const lessons = this.leaderContext?.lessons || [];
      return (this.students || []).reduce(
        (sum, student) => sum + readyToConfirm(student, lessons).length,
        0,
      );
    },
    ...mapGetters('courses', [
      'programs',
      'programResources',
      'lessonsByProgram',
      'lessonNumber',
      'isLeader',
      'view',
      'programAccess',
      'lessonLocked',
      'lessonHasAnswers',
      'programCanPurchase',
      'programCanSignUp',
      'programPurchaseBlockReason',
      'courseAccess',
      'programVisible',
      'programTier',
      'courseFeatures',
    ]),
    // Offered until the program is FULL — after that there is nothing a card
    // could add, and a redeem would only burn it for no gain.
    canEnterCode() {
      return this.programAccess(this.programSlug)?.tier !== 'FULL';
    },
    // Which program this landing renders — from the /courses/:programSlug route param.
    programSlug() {
      return this.$route.params.programSlug || 'next-steps-p1';
    },
    // Real name once the program list has loaded; humanizeProgramSlug
    // fallback for a cold render before that (e.g. 'next-steps-p1' ->
    // 'Next Steps Part 1'). Shared with QrConsent.vue's own programName.
    programName() {
      return resolveProgramName(this.programs, this.programSlug);
    },
    // Subtitle under the program title — varies by program; empty hides it.
    programSubtitle() {
      const subtitles = {
        'next-steps-p2': 'Starting Your Discipleship Journey',
        'next-steps-p1': 'Starting Your Christian Journey',
      };
      return subtitles[this.programSlug] || '';
    },
    // Per-program access summary from the server (visibility, tier, price, CTA).
    programSummary() {
      return (this.courseAccess?.programs || []).find(p => p.slug === this.programSlug) || null;
    },
    // Show the "Buy the book" CTA only when the server says this program is
    // purchasable for this member (not already FULL, prerequisites met) AND the
    // payment module is enabled.
    //
    // The flag is not redundant with the server's `canPurchase`: that answers
    // "may this member buy this program", not "is checkout live in this
    // environment". Without it the CTA opened the payment sheet as soon as the
    // unrelated `courses` flag went on, because `mobile-payments` gated the
    // payment *routes* and nothing else.
    canBuyBook() {
      const { isEnabled } = useFeatureFlags();
      // Through the store getter, exactly as the lesson viewer's canBuyProgram
      // does. Reading `programSummary.canPurchase` here meant the same server
      // fact arrived by two different routes, and the preview gate's unlock
      // button is only honoured if the two agree — when they don't, the member
      // taps it and is returned to a landing that refuses.
      return this.programCanPurchase(this.programSlug) && isEnabled(MOBILE_PAYMENTS_FLAG);
    },
    // May this member be taken INTO the purchase flow — the guard on the action,
    // where canBuyBook is the guard on the advertisement (ONE-8315).
    //
    // The two resolve to the same answer today — the server answers both
    // questions with one field; see programCanSignUp. They are kept apart
    // because different callers ask them: the banner asks whether to render
    // itself, the locked-card tap asks whether it may open a transaction. If the
    // server ever does split the two, only this one moves.
    //
    // Same feature flag, for the same reason — "may this member buy" and "is
    // checkout live in this environment" are still separate questions.
    canSignUpForCourse() {
      const { isEnabled } = useFeatureFlags();
      return this.programCanSignUp(this.programSlug) && isEnabled(MOBILE_PAYMENTS_FLAG);
    },
    // The server says this member may SEE the program but not buy it because of
    // their role (an N or V — purchaseBlockReason 'ROLE'). When they try to sign
    // up, they get the "interested to sign up?" modal instead of a payment sheet
    // that would be refused. Independent of the payments flag: the answer is
    // about who they are, not whether checkout is live in this environment.
    purchaseBlockedByRole() {
      return this.programPurchaseBlockReason(this.programSlug) === 'ROLE';
    },
    filteredLessons() {
      // Drop the no-lesson bucket (kind OTHER) so it never renders as a carousel
      // card. (GET /lessons already excludes it; this is defensive.)
      return this.lessonsByProgram(this.programSlug).filter(l => l.kind !== 'OTHER');
    },
    // Real lessons for this program — excludes the student/leader guide
    // entries, which are surfaced via the intro card, not as countable lessons.
    programLessons() {
      return this.sortedLessons.filter(l => l.kind === 'LESSON');
    },
    // Progress across this program's lessons — drives the segmented bar.
    totalLessons() {
      return this.programLessons.length;
    },
    // Answer-key mode (`prefillBlanks`): Regulars-and-above have already worked
    // through the class offline, which is why their blanks arrive pre-filled and
    // read-only. There is no per-lesson Complete tap and so no server
    // `completedAt`, so the progress bar must read them as fully done rather
    // than 0%. Same program-scoped flag the reader uses to prefill and skip the
    // unfilled-blanks gate (_lessonId.vue), keeping the two consistent.
    answerKeyMode() {
      return !!this.courseFeatures(this.programSlug)?.prefillBlanks;
    },
    completedCount() {
      if (this.answerKeyMode) return this.totalLessons;
      return this.programLessons.filter(l => !!(this.progress && this.progress[l.id]?.completedAt))
        .length;
    },
    // Lessons begun but not yet complete — the same device-local signal as the
    // carousel "In Progress" tag (lessonHasAnswers, keyed by slug). Drives the
    // half-weight in the bar and the "· N in progress" label suffix.
    inProgressCount() {
      // Answer-key mode counts every lesson as complete (see completedCount), so
      // none can also be "in progress" — otherwise the label reads
      // "5 / 5 Lessons · N in progress" and programProgressPercent exceeds 100%.
      if (this.answerKeyMode) return 0;
      return this.programLessons.filter(
        l => !this.progress?.[l.id]?.completedAt && this.lessonHasAnswers(l.slug),
      ).length;
    },
    // Continuous program progress (0–100). Each lesson is worth an equal share;
    // within a lesson: complete → full share, ≥1 blank filled → half share.
    // With 5 lessons that's 20% each → 0 / 10 / 20 per lesson.
    programProgressPercent() {
      if (!this.totalLessons) return 0;
      const points = this.completedCount + this.inProgressCount * 0.5;
      return Math.round((100 / this.totalLessons) * points);
    },
    // "3 / 5 Lessons · 2 in progress" — the suffix shows only when a lesson is
    // in progress. The leading count is completed lessons (server-driven).
    progressLabel() {
      const base = `${this.completedCount} / ${this.totalLessons} Lessons`;
      return this.inProgressCount ? `${base} · ${this.inProgressCount} in progress` : base;
    },
    // Order lessons by intra-program section (`part`), preserving the API order
    // within each part. Lessons without a part fall in with part 1.
    sortedLessons() {
      const part1 = this.filteredLessons.filter(l => l.part === 1 || l.part == null);
      const part2 = this.filteredLessons.filter(l => l.part === 2);
      if (part2.length === 0) return this.filteredLessons;
      return [...part1, ...part2];
    },
    // The program's guide lesson for the ACTIVE view, if it has one.
    //
    // The API decides by access — a leader's list contains the Leader's Guide — but
    // the student/leader toggle is a client-side view a leader can flip at will.
    // Matching on access alone therefore showed leaders the Leader's Guide while
    // they were previewing the student view. The kind has to match the view, not
    // just what the list happens to contain.
    introLesson() {
      const wantedKind = this.view === 'leader' ? 'LEADER_GUIDE' : 'GUIDE';
      return this.filteredLessons.find(l => l.kind === wantedKind) || null;
    },
    // Intro card: the guide lesson surfaced as the carousel hero. Title, slug and
    // thumbnail all come from the lesson row rather than being hardcoded — the copy
    // used to be baked in here, which left the card advertising "Intro to First
    // Steps" and "Leader's Introduction" long after the content had been renamed
    // and, in the Student's Guide's case, retired outright.
    //
    // Null when the program has no guide for this view; the carousel then simply
    // starts at Lesson 1.
    introCard() {
      const lesson = this.introLesson;
      if (!lesson) return null;
      const leader = lesson.kind === 'LEADER_GUIDE';
      return {
        id: lesson.id,
        title: lesson.title || '',
        label: leader ? "Leader's Guide" : "Student's Guide",
        path: `/courses/${lesson.programSlug || this.programSlug}/${lesson.slug}`,
        locked: false,
        completed: false,
        // Guide intro card — never carries a progress tag.
        status: 'none',
        buttonLabel: leader ? 'View Content' : 'Read Intro',
        // Matches Lovable --carousel-card-active (amber) — the hero tone.
        cardColor: '#E2992C',
        cardTextColor: '#1D212B',
        darkGlow: true,
        backgroundAnimation: lesson.backgroundAnimation || null,
        backgroundPoster: lesson.backgroundPoster || null,
      };
    },
    carouselCards() {
      // First Steps leads with the hardcoded intro card; that card is the hero,
      // so every lesson uses the golden tone. Other programs keep the first
      // lesson as the amber hero.
      const hasIntro = !!this.introCard;
      // Numbered lessons only. A guide is surfaced by the intro card or not at all —
      // when it isn't the one for the active view, it must not fall through into the
      // carousel as an ordinary lesson card carrying a "Lesson N" label.
      const lessons = this.programLessons;
      const lessonCards = lessons.map((lesson, i) => {
        // Shared getter keeps the card number in sync with the reader heading.
        const n = this.lessonNumber(lesson);
        const partLabel = lesson.part ? `Part ${lesson.part} · Lesson ${n}` : `Lesson ${n}`;
        const isHero = !hasIntro && i === 0;
        // Lock state comes from the server access summary (tier + per-lesson
        // locks), not the client. Locked-by-default until access loads. The
        // G-status preview lesson is returned unlocked (locked:false) so it
        // opens into the read-only, page-truncated reader.
        const locked = this.lessonLocked(lesson.programSlug || this.programSlug, lesson.slug);
        // Completion is server-driven (the reader's Complete button, which itself
        // gates on unfilled blanks) — our proxy for "all blanks filled". In
        // answer-key mode (Regulars) there is no per-lesson Complete tap and so no
        // `completedAt`, so treat every lesson as done — the same short-circuit the
        // progress bar's completedCount uses, keeping the carousel, the pill and the
        // bar consistent.
        const completed =
          this.answerKeyMode || !!(this.progress && this.progress[lesson.id]?.completedAt);
        // Card status tag + CTA per the Student's Guide spec:
        //   all blanks filled (completed) → "Complete"    → "Revisit Lesson"
        //   ≥1 blank filled, not complete → "In Progress" → "View Lesson"
        //   no blanks filled              → (no tag)       → "Begin Lesson"
        // Leader's Guide: never a status tag, CTA always "View Lesson".
        const leader = this.view === 'leader';
        const inProgress = !leader && !completed && !locked && this.lessonHasAnswers(lesson.slug);
        let status = 'none';
        let buttonLabel;
        if (leader) {
          buttonLabel = 'View Lesson';
        } else if (completed) {
          status = 'complete';
          buttonLabel = 'Revisit Lesson';
        } else if (inProgress) {
          status = 'inProgress';
          buttonLabel = 'View Lesson';
        } else {
          buttonLabel = 'Begin Lesson';
        }
        return {
          id: lesson.id,
          title: lesson.title || '',
          label: partLabel,
          // Structured lesson number for the carousel's top-right pill — so it
          // reads the number directly instead of regex-parsing it back out of the
          // display label (the guide intro card has none and shows no pill).
          lessonNumber: n,
          // Program-scoped lesson route — opens the existing lesson reader.
          path: `/courses/${lesson.programSlug || this.programSlug}/${lesson.slug}`,
          locked,
          completed,
          status,
          buttonLabel,
          // Matches Lovable: hero = --carousel-card-active (#E2992C amber),
          // remaining = --carousel-card-hero (#FFD11A golden yellow)
          cardColor: isHero ? '#E2992C' : '#FFD11A',
          // Matches Lovable --carousel-ink — dark text reads clearly on both warm tones
          cardTextColor: '#1D212B',
          // First card gets a white glow in dark mode
          darkGlow: isHero,
          // Per-lesson background animation (mocked until the API serves it).
          backgroundAnimation: lesson.backgroundAnimation,
          // Its first-frame still, derived alongside the animation in the store.
          backgroundPoster: lesson.backgroundPoster,
        };
      });
      return hasIntro ? [this.introCard, ...lessonCards] : lessonCards;
    },
  },
  watch: {
    // Student and leader hold a different number of cards — the guide intro card
    // exists for one view but not the other, so toggling changes carouselCards.length.
    // carouselIndex is a fixed local value only ever advanced by the carousel; nothing
    // pulls it back. So a leader parked on the last card who toggles to student (fewer
    // cards) leaves carouselIndex pointing past the new end, and the carousel centres
    // nothing — the empty-slot glitch. Clamp into range so the toggle lands on the last
    // card that now exists.
    'carouselCards.length'(length) {
      const lastIndex = Math.max(0, length - 1);
      if (this.carouselIndex > lastIndex) this.carouselIndex = lastIndex;
    },
  },
  async ionViewWillEnter() {
    await this.$nextTick();
    rebindCoursesMenu(this.pageMenuId, 'courses-list-content');

    // Keep the store in sync so the shared sidebar reflects this program.
    this.$store.commit('courses/setCurrentProgramSlug', this.programSlug);

    // Fire-and-forget: leader status drives the optional toggle; never block
    // the lesson carousel on it.
    this.$store.dispatch('courses/checkLeaderAccess');

    // Same treatment for discipling eligibility, which decides whether the
    // Leader's Dashboard card appears. It fails closed — a 404 (flag off) or a
    // 403 leaves canDisciple false and the card hidden — so a slow or absent
    // response can only under-show, never wrongly reveal.
    // Promise.resolve: a stubbed store may return the context synchronously.
    Promise.resolve(this.$store.dispatch('courses/fetchLeaderContext'))
      .then(context => {
        // The roster is what the card's "N to confirm" badge counts. Only a
        // discipling leader has one, and the badge is optional — a failed read
        // costs the number, never the card or the lessons.
        if (context?.canDisciple) {
          return this.$store.dispatch('courses/fetchStudents').catch(() => {});
        }
        return undefined;
      })
      .catch(() => {});

    // ionViewWillEnter fires on EVERY entry, including the pop back from a
    // lesson. The store already holds the lessons the body renders from, so
    // tearing it down for a spinner while cached data refreshes reads as a white
    // flash the instant the back animation lands. Only block on a cold load with
    // nothing to show; otherwise refresh underneath the rendered content.
    this.loading = this.lessons.length === 0;
    try {
      await Promise.all([
        this.$store.dispatch('courses/fetchPrograms'),
        this.$store.dispatch('courses/fetchLessons'),
        this.$store.dispatch('courses/fetchCourseAccess'),
        this.$store.dispatch('courses/loadUserAnswersFromStorage'),
        // Leader resources for the cards under the carousel. In the same batch
        // because it is a cheap summary read, and the action swallows its own
        // errors — a failure costs the card, never the lessons.
        this.$store.dispatch('courses/fetchProgramResources', {
          programSlug: this.programSlug,
        }),
      ]);

      // Secondary visibility guard. The all-page entry buttons are gated by the
      // CMS; this catches a member who deep-links to a program they can't see
      // (e.g. N/V → First Steps, or a non-leader below I → Next Steps). Only
      // redirects when access loaded AND the program is explicitly not visible,
      // so a transient access-fetch failure never locks anyone out.
      //
      // A program the member HOLDS is never bounced, whatever `visible` says —
      // the same allowance coursesGuard makes (hasActiveProgramGrant). An N/V who
      // unlocked the book card has FULL but is below the G+ visibility band, and
      // checking `visible` alone sent them Home straight after the scan spent
      // their card. Hidden while `courses-nv-visibility` was on; exposed when
      // hogc-one-api #1203 removed it.
      if (
        this.courseAccess &&
        !this.programVisible(this.programSlug) &&
        this.programTier(this.programSlug) === 'PREVIEW'
      ) {
        this.$router.replace(AUTHENTICATED_HOME_PATH);
        return;
      }
      // Completed badges on the carousel — optional, never blocks the list.
      this.$store.dispatch('courses/fetchProgress').catch(() => {});
    } catch (error) {
      console.error('Failed to load Courses data:', error);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    // The sign-up-interest modal is dismissed (its "Got it" button or a backdrop
    // tap, both of which emit `close`). Lower the flag — the modal is bound
    // one-way, so this handler is the only thing that closes it — and return the
    // member to the lesson carousel if they are no longer on it (it lives here).
    onPurchaseIneligibleClose() {
      this.showPurchaseIneligibleModal = false;
      const landingPath = `/courses/${this.programSlug}`;
      if (this.$route.path !== landingPath) {
        this.$router.replace(landingPath);
      }
    },
    // A code redeemed from this landing may unlock the *other* part, so navigate
    // rather than assume we are already in the right place. Refreshing access is
    // the store action's job; it has already happened by the time this fires.
    onRedeemed(programSlug) {
      if (programSlug && programSlug !== this.programSlug) {
        this.$router.push(`/courses/${programSlug}`);
      }
    },
    // The store owns the flag and its persistence, so the choice reaches the
    // lesson reader and notes hub too — as page-local state it never could.
    toggleTheme() {
      this.$store.commit('courses/setDark', !this.isDark);
    },
    setView(view) {
      this.$store.commit('courses/setView', view);
    },
    // Back to whatever opened this landing — the Next Steps chooser in the usual
    // flow, but a pop also gets a deep link from a notification or the All page
    // right. `/courses` is the cold-open fallback only.
    handleBack() {
      this.goBack(NEXT_STEPS_CHOOSER_PATH);
    },
    openLessonPath(path) {
      // Empty path = card not wired to a route yet; guard against no-op navigation.
      if (!path) return;
      this.$router.push(path);
    },
    goToLeaderDashboard() {
      this.$router.push(`/courses/${this.programSlug}/leader`);
    },
    openResource(resource) {
      this.$router.push(`/courses/${this.programSlug}/resource/${resource.slug}`);
    },
    // Buy the physical book → run the payment module → on success the server grants
    // FULL access instantly (confirmPurchase) AND issues a "collect your book"
    // voucher, which we surface by refreshing My Vouchers. Framing the purchase as
    // a physical good (with digital access as a companion) keeps it clear of Apple
    // IAP; see documentation/HOGC_ONE_FLOWS.md §G2.
    async handleBuyBook() {
      // Belt and braces with the v-if on canBuyBook: this opens a real
      // transaction, so it must not be reachable by a stale render or a handler
      // fired from anywhere else.
      //
      // Guarded on canSignUpForCourse, NOT canBuyBook: the banner is only one of
      // this method's callers — the locked-card tap reaches it too, and neither
      // should be gated on whether the *banner* would have rendered (ONE-8315).
      // The two conditions agree today, so the banner's own CTA behaves exactly
      // as before.
      if (this.isPurchasing) return;
      if (!this.canSignUpForCourse) {
        // An N/V can see the course but is not eligible to buy it: invite them to
        // ask a HOGC leader instead of silently doing nothing. Any other reason
        // (checkout off in this environment, etc.) still no-ops here — the buy
        // banner never renders in those cases, and the locked-card tap that
        // reaches this method has nothing to show.
        if (this.purchaseBlockedByRole) this.showPurchaseIneligibleModal = true;
        return;
      }
      this.isPurchasing = true;
      const slug = this.programSlug;
      // Fallback only, and only for servers older than the field: the open now
      // returns amountCents — the figure written to the ledger row — which is
      // the one thing safe to quote. This cached summary is a catalog read from
      // whenever the page last loaded, and showing one number while charging
      // another is worse than showing nothing. Do not delete it as dead code
      // until every deployed API returns amountCents.
      const cachedPriceCents = this.programSummary?.priceCents;
      try {
        const purchase = await this.$store.dispatch('courses/purchaseProgram', slug);
        const reference = purchase?.reference;
        if (!reference) throw new Error('Could not start the purchase.');

        const serverPriceCents =
          typeof purchase?.amountCents === 'number' ? purchase.amountCents : cachedPriceCents;

        const result = await this.$store.dispatch('payment/openPaymentSheet', {
          amount: typeof serverPriceCents === 'number' ? serverPriceCents / 100 : 0,
          description: `${this.programName} book`,
          referenceCode: reference,
          // The transaction is already open server-side; the sheet presents the
          // hosted checkout it minted rather than opening a second one.
          displayReference: purchase?.displayReference ?? null,
          checkoutUrl: purchase?.checkoutUrl ?? null,
          category: 'course-program',
          // Purchases opt out of the bank rail until a FAST transfer settles
          // against the payment ledger (epic 86eynr4tx). Today it would debit
          // the member and never unlock the product.
          allowBankRail: false,
          allowFeesCoverage: false,
          metadata: { programSlug: slug },
        });

        if (result?.status !== PaymentStatus.SUCCESS) return;

        // Server re-verifies PAID and grants FULL only when actually paid; it
        // refreshes the access summary on grant (unlocks the lessons).
        // Identity, not arithmetic. Comparing counts asked "are there more
        // vouchers than before", which answers a different question than "did
        // MY book turn up": a member who already owned an unrelated voucher and
        // had never opened My Vouchers this session compared a fetched wallet
        // against an initial empty array, so any row at all read as arrival and
        // the message promised a book that was still on the retry queue.
        //
        // `hasLoaded` is the other half. Without it an empty `vouchers` array is
        // ambiguous — no vouchers, or never fetched — and every id looks new on
        // the first load. When we cannot know, we say the softer thing rather
        // than guess; "will appear shortly" is true either way.
        const walletLoaded = this.$store.state.vouchers.hasLoaded;
        const idsBefore = new Set(this.$store.state.vouchers.vouchers.map(v => v.id));
        const confirmation = await this.$store.dispatch('courses/confirmPurchase', reference);
        // Awaited, not fired and forgotten: whether the voucher actually arrived
        // is the difference between the two messages below.
        await this.$store.dispatch('vouchers/fetchVouchers').catch(() => {});
        const voucherArrived =
          walletLoaded && this.$store.state.vouchers.vouchers.some(v => !idsBefore.has(v.id));

        // `granted` answers ONE question: did the course entitlement get written.
        // Voucher issuance is a separate listener on the same payment event and
        // can fail (no partner token, an unpriced voucher, out of stock) or still
        // be on its retry queue — so this used to promise "your book voucher is
        // in My Vouchers" on the strength of an unrelated success, and a member
        // whose mint was still queued opened an empty wallet.
        let message;
        if (!confirmation?.granted) {
          message = 'Payment received — confirming your access now.';
        } else if (voucherArrived) {
          message = `${this.programName} unlocked — your book voucher is in My Vouchers.`;
        } else {
          message = `${this.programName} unlocked. Your book voucher will appear in My Vouchers shortly.`;
        }
        const toast = await toastController.create({
          message,
          duration: 3500,
          position: 'top',
          color: 'dark',
          cssClass: 'courses-toast',
        });
        await toast.present();
      } catch (error) {
        const toast = await toastController.create({
          // Prefer what the server said — "this isn't on sale right now" is
          // actionable, "something went wrong" is not.
          message:
            error?.response?.data?.message ||
            error?.message ||
            'Something went wrong starting your purchase.',
          duration: 3000,
          position: 'top',
          color: 'dark',
          cssClass: 'courses-toast',
        });
        await toast.present();
      } finally {
        this.isPurchasing = false;
      }
    },
    // Tapping a locked lesson. The card's CTA reads "Sign up", so for anyone who
    // may actually sign up this opens the flow. It used to only ever toast —
    // including at the moment the member had just asked to buy — which left them
    // re-tapping a button that described an action it refused to take (ONE-8315).
    //
    // EVERY locked lesson, trial ones included. The trial lessons (Part 1 lessons
    // 3 & 4) used to be answered before the purchase question, with a toast
    // pointing at the class QR: the tap did have another route to it, so buying
    // was not the only way in. But the button says "Sign up", and refusing to sign
    // someone up is not a better answer for being a true one — a member who taps it
    // is asking to buy, whichever lesson they tapped. The class QR still unlocks a
    // trial lesson; it is simply not what this button means.
    async handleLockedTap() {
      if (this.canSignUpForCourse) {
        await this.handleBuyBook();
        return;
      }
      // An N/V taps a locked lesson's "Sign up": they can see the course but are
      // not eligible to buy it, so invite them to ask a HOGC leader rather than
      // toasting a neutral "not available" message.
      if (this.purchaseBlockedByRole) {
        this.showPurchaseIneligibleModal = true;
        return;
      }
      await this.presentLockedToast();
    },
    // Why the tap could not be turned into a sign-up — driven by the server's
    // purchaseBlockReason so the copy names the real cause. (The ROLE reason has
    // its own bookstore modal and never reaches here.)
    //
    // Kept separate from presenting it: the copy is the part that was wrong, and
    // a pure string is the part a test can hold to account.
    //
    // Reached only when the sign-up flow could NOT be opened, so it never speaks
    // for a lesson that has one — which is why it no longer takes the lesson's
    // access summary. There is nothing left here that varies per lesson.
    lockedLessonMessage() {
      if (this.programCanSignUp(this.programSlug)) {
        // The server would sell to them; only the payment module being off in
        // this environment stopped the flow from opening.
        return 'Unlock the full course to access all lessons.';
      }
      if (this.programPurchaseBlockReason(this.programSlug) === 'PREREQUISITE') {
        // Part 2 needs Part 1 first — the server tells us this directly now,
        // rather than the message re-deriving it from the program slug.
        return 'Complete Next Steps Part 1 to continue your journey.';
      }
      // Any other refusal. Deliberately neutral: naming the wrong reason is what
      // caused the ONE-8315 bug, and the specific ones above are already handled.
      return "Sign-ups aren't available for your account yet.";
    },
    async presentLockedToast() {
      const toast = await toastController.create({
        message: this.lockedLessonMessage(),
        duration: 3000,
        position: 'top',
        color: 'dark',
        cssClass: 'courses-toast',
      });
      await toast.present();
    },
  },
};
</script>

<style scoped>
.courses-toast {
  --border-radius: 12px;
}
</style>
